import { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  createCroppedImageFile,
  getImageDimensions,
  normalizeImageFileForUpload,
} from '~/components/Common/Images/cropUtils';

import { MAX_SVC_IMAGE_UPLOAD_BYTES, getUploadedImageId, uploadImage } from '~/services/ImageService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import {
  type LocalImageEdit,
  buildDefaultEdit,
  getImageCropPixels,
  hasImagePixelEdit,
} from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { MediaImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  canPersistRoomImageHiddenState,
  findPersistedRoomByKey,
  getMediaImageSourceKey,
  removeImageOverride,
} from './ImagesVideo/utils';

const IMAGE_EDIT_SOURCE_WIDTH = 2400;

interface UseOfferBuilderMediaEditsParams {
  offerId: string;
  mediaImages: Array<MediaImage>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  setHiddenImageOverrides: (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void;
  replaceUploadedImage: (publicId: string, nextImage: MediaImage) => void;
}

export interface OfferBuilderMediaEditsController {
  imageEdits: Record<string, LocalImageEdit>;
  imagePublicIdReplacements: Record<string, string>;
  savedImageTitles: Record<string, string>;
  roomImagePendingEdit?: MediaImage;
  unsavedRoomImageTitleEdits: Array<string>;
  hasUnsavedRoomImageTitleEdits: boolean;
  clearImageEdit: (publicId: string) => void;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  editRoomImage: (publicId: string) => void;
  saveRoomImageTitleEdits: (roomKey: string) => Promise<void>;
  saveImageEdit: (publicId: string, edit: LocalImageEdit, persistedPublicId?: string) => Promise<void>;
  updateImageTitle: (roomKey: string, publicId: string, title: string) => void;
  discardRoomImageTitleEdits: () => void;
}

export default function useOfferBuilderMediaEdits({
  offerId,
  mediaImages,
  propertyRoomTypes,
  setHiddenImageOverrides,
  replaceUploadedImage,
}: UseOfferBuilderMediaEditsParams): OfferBuilderMediaEditsController {
  const { enqueueSnackbar } = useSnackbar();
  const [imagePublicIdReplacements, setImagePublicIdReplacements] = useState<Record<string, string>>({});
  const [roomImagePendingEdit, setRoomImagePendingEdit] = useState<MediaImage | undefined>();
  const [imageEdits, setImageEdits] = useState<Record<string, LocalImageEdit>>({});
  const [savedImageTitles, setSavedImageTitles] = useState<Record<string, string>>({});

  const withCurrentTitle = (image: MediaImage): MediaImage => {
    const imageSourceKey = getMediaImageSourceKey(image);
    const imageEdit = imageEdits[imageSourceKey] ?? imageEdits[image.publicId];

    return { ...image, title: imageEdit?.title ?? savedImageTitles[imageSourceKey] ?? image.title };
  };

  const unsavedRoomImageTitleEdits = mediaImages.flatMap((image) => {
    const imageSourceKey = getMediaImageSourceKey(image);
    const imageEdit = imageEdits[imageSourceKey];
    const nextTitle = imageEdit?.title ?? '';
    const persistedTitle =
      savedImageTitles[imageSourceKey] ??
      findPersistedRoomByKey(propertyRoomTypes, image.sourceRoomId)?.images?.find(
        (sourceImage) =>
          String(sourceImage.id) === image.sourceImageId || sourceImage.id_cloudinary_external === image.publicId,
      )?.title ??
      '';

    if (!imageEdit || image.sourceType !== 'room' || !canPersistRoomImageHiddenState(image)) {
      return [];
    }

    return nextTitle === persistedTitle ? [] : [imageSourceKey];
  });

  const clearImageEdit = (publicId: string) => {
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      delete nextEdits[publicId];

      return nextEdits;
    });
  };

  const editRoomImage = (publicId: string) => {
    const image =
      mediaImages.find(
        (mediaImage) => mediaImage.publicId === publicId && canPersistRoomImageHiddenState(mediaImage),
      ) ?? mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setRoomImagePendingEdit(withCurrentTitle(image));
    }
  };

  const saveRoomImageTitleEdits = async (roomKey: string) => {
    const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
    const editedRoomImages = mediaImages.flatMap((image) => {
      const imageSourceKey = getMediaImageSourceKey(image);
      const imageEdit = imageEdits[imageSourceKey];
      const nextTitle = imageEdit?.title ?? '';
      const persistedTitle =
        savedImageTitles[imageSourceKey] ??
        room?.images?.find(
          (sourceImage) =>
            String(sourceImage.id) === image.sourceImageId || sourceImage.id_cloudinary_external === image.publicId,
        )?.title ??
        '';

      if (
        image.sourceRoomId !== roomKey ||
        !imageEdit ||
        !canPersistRoomImageHiddenState(image) ||
        nextTitle === persistedTitle
      ) {
        return [];
      }

      return [{ ...image, title: nextTitle }];
    });

    if (editedRoomImages.length === 0) {
      return;
    }

    await Promise.all(
      editedRoomImages.map((image) =>
        ReservationService.updateRoomTypeImage(
          {
            id_cloudinary_external: image.publicId,
            title: image.title,
          },
          image.sourceImageId,
          image.sourcePropertyId,
          image.sourceRoomTypeId,
        ),
      ),
    );

    setSavedImageTitles((currentTitles) => ({
      ...currentTitles,
      ...Object.fromEntries(editedRoomImages.map((image) => [getMediaImageSourceKey(image), image.title])),
    }));
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      editedRoomImages.forEach((image) => {
        delete nextEdits[getMediaImageSourceKey(image)];
      });

      return nextEdits;
    });
  };

  const saveImageEdit = async (publicId: string, edit: LocalImageEdit, persistedPublicId?: string) => {
    const image =
      roomImagePendingEdit?.publicId === publicId
        ? roomImagePendingEdit
        : mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    const shouldUploadImage = hasImagePixelEdit(edit);
    let nextPublicId = shouldUploadImage ? publicId : (persistedPublicId ?? publicId);

    if (shouldUploadImage) {
      try {
        nextPublicId = await uploadEditedImage(publicId, edit);
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, 'Failed to save image edits'), { variant: 'error' });
        throw error;
      }
    }

    const persistedImageUpdate = canPersistRoomImageHiddenState(image)
      ? {
          promise: ReservationService.updateRoomTypeImage(
            {
              id_cloudinary_external: nextPublicId,
              title: edit.title,
            },
            image.sourceImageId,
            image.sourcePropertyId,
            image.sourceRoomTypeId,
          ),
          errorMessage: 'Failed to update room image',
          successMessage: 'Room image updated',
        }
      : image.sourceType === 'offer' && image.sourceImageId
        ? {
            promise: OffersService.updateImage(offerId, image.sourceImageId, {
              id_cloudinary_external: nextPublicId,
              order: image.order,
              title: edit.title,
            }),
            errorMessage: 'Failed to update image',
            successMessage: 'Image updated',
          }
        : undefined;

    if (persistedImageUpdate) {
      try {
        await persistedImageUpdate.promise;
        enqueueSnackbar(persistedImageUpdate.successMessage, { variant: 'success' });
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, persistedImageUpdate.errorMessage), { variant: 'error' });
        throw error;
      }
    }

    if (nextPublicId !== publicId) {
      const nextImage = {
        ...image,
        originalPublicId: image.originalPublicId ?? image.publicId,
        publicId: nextPublicId,
        title: edit.title || undefined,
      };
      const replacedImageSourceKey = getMediaImageSourceKey(image);

      setImagePublicIdReplacements((currentReplacements) => ({
        ...currentReplacements,
        [publicId]: nextPublicId,
      }));
      replaceUploadedImage(replacedImageSourceKey, nextImage);
      setHiddenImageOverrides((currentOverrides) => {
        const previousOverride = currentOverrides[publicId];

        if (previousOverride === undefined) {
          return currentOverrides;
        }

        return {
          ...removeImageOverride(currentOverrides, publicId),
          [nextPublicId]: previousOverride,
        };
      });
      setImageEdits((currentEdits) => {
        const nextEdits = { ...currentEdits };

        delete nextEdits[publicId];

        nextEdits[nextPublicId] = buildDefaultEdit(nextImage);

        return nextEdits;
      });
      return;
    }

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: edit,
    }));
  };

  const updateImageTitle = (roomKey: string, publicId: string, title: string) => {
    const image = mediaImages.find(
      (mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceRoomId === roomKey,
    );

    if (!image) {
      return;
    }

    const imageSourceKey = getMediaImageSourceKey(image);

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [imageSourceKey]: {
        ...(currentEdits[imageSourceKey] ?? buildDefaultEdit(image)),
        title,
      },
    }));
  };

  const discardRoomImageTitleEdits = () => {
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      unsavedRoomImageTitleEdits.forEach((imageSourceKey) => {
        delete nextEdits[imageSourceKey];
      });

      return nextEdits;
    });
  };

  return {
    imageEdits,
    imagePublicIdReplacements,
    savedImageTitles,
    roomImagePendingEdit,
    unsavedRoomImageTitleEdits,
    hasUnsavedRoomImageTitleEdits: unsavedRoomImageTitleEdits.length > 0,
    clearImageEdit,
    setRoomImagePendingEdit,
    editRoomImage,
    saveRoomImageTitleEdits,
    saveImageEdit,
    updateImageTitle,
    discardRoomImageTitleEdits,
  };
}

async function uploadEditedImage(publicId: string, edit: LocalImageEdit) {
  const sourceFile = await fetchImageFile(publicId, edit.filename);
  const dimensions = await getImageDimensions(sourceFile);
  const croppedFile = await createCroppedImageFile({
    adjustments: {
      brightness: edit.brightness,
      contrast: edit.contrast,
      saturation: edit.saturation,
      warmth: edit.warmth,
    },
    croppedAreaPixels: getImageCropPixels(edit, dimensions),
    file: sourceFile,
    outputFileName: edit.filename,
  });
  const uploadFile = await normalizeImageFileForUpload({
    file: croppedFile,
    maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
    outputFileName: edit.filename,
  });
  const response = await uploadImage(uploadFile);
  const uploadedImageId = getUploadedImageId(response);

  if (!uploadedImageId) {
    throw new Error('Image service did not return an image id');
  }

  return uploadedImageId;
}

async function fetchImageFile(publicId: string, filename: string) {
  const response = await fetch(
    `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:good,c_limit,w_${IMAGE_EDIT_SOURCE_WIDTH}/${publicId}`,
  );

  if (!response.ok) {
    throw new Error('Failed to load image for editing');
  }

  const blob = await response.blob();

  return new File([blob], filename, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}
